import React, { FC, useCallback } from 'react';
import { CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Box, Tooltip } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useTheme } from '@mui/material/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash } from '@fortawesome/free-solid-svg-icons';
import { Prompt } from '../types';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme) => ({
  tableWrapper: {
    width: '100%',
    overflowX: 'auto',
    overflowY: 'hidden',
  },
  table: {
    minWidth: 400,
    [theme.breakpoints.down('md')]: {
      minWidth: 0,
      width: '100%',
    },
  },
  mobilePromptCell: {
    display: 'none',
    [theme.breakpoints.down('md')]: {
      display: 'table-cell',
    },
  },
  desktopPromptCell: {
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  mobilePrompt: {
    display: 'flex',
    flexDirection: 'column',
  },
  mobilePromptName: {
    fontWeight: 500,
  },
  actionsCell: {
    whiteSpace: 'nowrap',
  },
  modelCell: {
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
}));

const getFirstParagraph = (text: string | undefined): string => {
  if (!text) return '';
  const paragraphs = text.split(/\n\n|\r\n\r\n/);
  return paragraphs[0] || '';
};

interface PromptsTableProps {
  prompts: Prompt[];
  isLoading: boolean;
  onEdit: (prompt: Prompt) => void;
  onDelete: (promptId: string) => void;
}

export const PromptsTable: FC<PromptsTableProps> = ({ prompts, isLoading, onEdit, onDelete }) => {
  const classes = useStyles();
  const theme = useTheme();
  const { t } = useTranslation();

  const handleEdit = useCallback((prompt: Prompt) => {
    onEdit(prompt);
  }, [onEdit]);

  const handleDelete = useCallback((e: React.MouseEvent, promptId: string) => {
    e.stopPropagation();
    onDelete(promptId);
  }, [onDelete]);

  const displayDescription = (description: string | undefined): string => {
    return getFirstParagraph(description);
  };

  if (isLoading) {
    return (
      <Box className={classes.tableWrapper}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <CircularProgress size={20} />
                </TableCell>
                <TableCell className={classes.modelCell}>
                  <CircularProgress size={20} />
                </TableCell>
                <TableCell>
                  <CircularProgress size={20} />
                </TableCell>
                <TableCell>
                  <CircularProgress size={20} />
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>
                  <CircularProgress size={20} />
                </TableCell>
                <TableCell className={classes.modelCell}>
                  <CircularProgress size={20} />
                </TableCell>
                <TableCell>
                  <CircularProgress size={20} />
                </TableCell>
                <TableCell>
                  <CircularProgress size={20} />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }

  return (
    <Box className={classes.tableWrapper}>
      <TableContainer component={Paper}>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell className={classes.mobilePromptCell}>{t('aiConfig:prompt')}</TableCell>
              <TableCell className={classes.desktopPromptCell}>{t('aiConfig:promptName')}</TableCell>
              <TableCell className={classes.modelCell}>{t('aiConfig:promptModel')}</TableCell>
              <TableCell className={classes.desktopPromptCell}>{t('aiConfig:description')}</TableCell>
              <TableCell className={classes.actionsCell}>{t('aiConfig:actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {prompts.map((prompt) => (
              <TableRow key={prompt._id}>
                <TableCell className={classes.mobilePromptCell}>
                  <Box className={classes.mobilePrompt}>
                    <span className={classes.mobilePromptName}>{prompt.name}</span>
                    <span>{getFirstParagraph(prompt.english?.description)}</span>
                  </Box>
                </TableCell>
                <TableCell className={classes.desktopPromptCell}>{prompt.name}</TableCell>
                <TableCell className={classes.modelCell}>{prompt.model}</TableCell>
                <TableCell className={classes.desktopPromptCell}>{displayDescription(prompt.english?.description)}</TableCell>
                <TableCell className={classes.actionsCell}>
                  <Box style={{ display: 'flex', gap: theme.spacing(0.5), whiteSpace: 'nowrap' }}>
                    <Tooltip title={t('aiConfig:editPrompt')}>
                      <IconButton size="small" onClick={() => handleEdit(prompt)}>
                        <FontAwesomeIcon icon={faEdit} color={theme.palette.secondary.main} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('aiConfig:delete')}>
                      <IconButton size="small" onClick={(e) => handleDelete(e, prompt._id)} color="secondary">
                        <FontAwesomeIcon icon={faTrash} size="xs" color={theme.palette.error.main} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
